"use client"

import { CopyableText } from "@/components/ui/copyable-text"
import { useRef, useEffect } from "react"
import { useOrchestrator, type WorkflowState, type WorkflowLog } from "@/contexts/OrchestratorContext"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { SpinnerGapIcon as Loader2 } from "@phosphor-icons/react/dist/csr/SpinnerGap"
import { CheckCircleIcon as CheckCircle2 } from "@phosphor-icons/react/dist/csr/CheckCircle"
import { XCircleIcon as XCircle } from "@phosphor-icons/react/dist/csr/XCircle"
import { XIcon as X } from "@phosphor-icons/react/dist/csr/X"
import { BrainIcon as Brain } from "@phosphor-icons/react/dist/csr/Brain"
import { WrenchIcon as Wrench } from "@phosphor-icons/react/dist/csr/Wrench"
import { ChatIcon as MessageSquare } from "@phosphor-icons/react/dist/csr/Chat"
import { ClockIcon as Clock } from "@phosphor-icons/react/dist/csr/Clock"
import ReactMarkdown from "react-markdown"

function formatElapsed(startedAt: string): string {
  const ms = Date.now() - new Date(startedAt).getTime()
  const s = Math.floor(ms / 1000)
  if (s < 60) return `${s}s`
  const m = Math.floor(s / 60)
  return `${m}m ${s % 60}s`
}

function StatusIcon({ status }: { status: WorkflowState["status"] }) {
  if (status === "running") return <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />
  if (status === "completed") return <CheckCircle2 className="h-5 w-5 text-emerald-500" />
  return <XCircle className="h-5 w-5 text-red-500" />
}

function statusLabel(status: WorkflowState["status"]) {
  if (status === "running") return "Running"
  if (status === "completed") return "Completed"
  return "Failed"
}

function statusColor(status: WorkflowState["status"]) {
  if (status === "running") return "bg-accent text-primary border-blue-200"
  if (status === "completed") return "bg-emerald-100 text-emerald-700 border-emerald-200"
  return "bg-red-100 text-red-700 border-red-200"
}

function safeMd(value: unknown): string {
  if (!value) return ""
  if (typeof value === "string") return value
  try { return JSON.stringify(value, null, 2) } catch { return String(value) }
}

function LogEntry({ log }: { log: WorkflowLog }) {
  if (!log || !log.type) return null

  const ts = log.ts
    ? new Date(log.ts).toLocaleTimeString("vi-VN", { hour: "2-digit", minute: "2-digit", second: "2-digit" })
    : ""

  if (log.type === "reasoning") {
    return (
      <div className="flex items-start gap-2 px-3 py-1.5 rounded-md bg-purple-50/60 border border-purple-100">
        <Brain className="h-3.5 w-3.5 text-purple-400 flex-shrink-0 mt-0.5" />
        <p className="flex-1 text-[11px] text-purple-700 italic leading-relaxed whitespace-pre-wrap break-words">{String(log.content || "")}</p>
        <span className="text-[10px] text-purple-300 flex-shrink-0 tabular-nums">{ts}</span>
      </div>
    )
  }

  if (log.type === "tool_start") {
    let inputPreview = ""
    try {
      inputPreview = log.tool_input
        ? (typeof log.tool_input === "string" ? log.tool_input : JSON.stringify(log.tool_input)).slice(0, 80)
        : ""
    } catch { inputPreview = "" }

    return (
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-indigo-50/60 border border-indigo-100">
        <Wrench className="h-3.5 w-3.5 text-indigo-500 flex-shrink-0" />
        <span className="text-[11px] font-semibold text-indigo-700">{log.tool_name || "tool"}</span>
        {inputPreview && (
          <span className="text-[10px] text-indigo-400 truncate max-w-[200px]">{inputPreview}</span>
        )}
        <span className="text-[10px] text-indigo-300 ml-auto tabular-nums flex-shrink-0">{ts}</span>
      </div>
    )
  }

  if (log.type === "tool_result") {
    const resultStr = log.tool_result ? String(log.tool_result) : ""
    const isError = log.tool_status === "error" || resultStr.includes('"status": "failed"') || resultStr.includes('"status":"failed"')
    return (
      <div className={`flex items-start gap-2 px-3 py-1.5 rounded-md ${isError ? "bg-red-50/60 border border-red-100" : "bg-emerald-50/40 border border-emerald-100"}`}>
        <Wrench className={`h-3.5 w-3.5 flex-shrink-0 mt-0.5 ${isError ? "text-red-400" : "text-emerald-400"}`} />
        <span className={`text-[11px] flex-1 break-words ${isError ? "text-red-600" : "text-emerald-700"}`}>
          {resultStr ? resultStr.slice(0, 200) : isError ? "Error" : "OK"}
        </span>
        <span className="text-[10px] text-gray-300 flex-shrink-0 tabular-nums">{ts}</span>
      </div>
    )
  }

  if (log.type === "text") {
    return (
      <div className="flex items-start gap-2 px-3 py-1.5">
        <MessageSquare className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0 mt-0.5" />
        <div className="flex-1 text-[11px] text-secondary-foreground leading-relaxed prose prose-xs max-w-none break-words">
          <ReactMarkdown>{safeMd(log.content)}</ReactMarkdown>
        </div>
        <span className="text-[10px] text-gray-300 flex-shrink-0 tabular-nums">{ts}</span>
      </div>
    )
  }

  if (log.type === "done") {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-emerald-50/60 border border-emerald-100">
        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />
        <span className="text-[11px] font-semibold text-emerald-700">Done</span>
        <span className="text-[10px] text-emerald-300 ml-auto tabular-nums">{ts}</span>
      </div>
    )
  }

  return null
}

function WorkflowCard({ workflow }: { workflow: WorkflowState }) {
  const { dismissWorkflow } = useOrchestrator()
  const logsEndRef = useRef<HTMLDivElement>(null)
  const isTerminal = workflow.status === "completed" || workflow.status === "failed"

  useEffect(() => {
    if (workflow.status === "running") {
      logsEndRef.current?.scrollIntoView({ behavior: "smooth" })
    }
  }, [workflow.logs.length, workflow.status])

  const borderClass =
    workflow.status === "running" ? "border-blue-200" :
    workflow.status === "completed" ? "border-emerald-200" :
    "border-red-200"

  return (
    <div className={`rounded-xl border ${borderClass} bg-card overflow-hidden`}>
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
        <StatusIcon status={workflow.status} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-foreground truncate"><CopyableText value={workflow.carId} label="mã xe" /></span>
            <span className={`inline-flex items-center text-[10px] font-medium px-2 py-0.5 rounded-full border ${statusColor(workflow.status)}`}>
              {statusLabel(workflow.status)}
            </span>
          </div>
          <div className="flex items-center gap-1 mt-0.5 text-[11px] text-muted-foreground">
            <Clock className="h-3 w-3" />
            <span className="tabular-nums">{formatElapsed(workflow.startedAt)}</span>
          </div>
        </div>
        {isTerminal && (
          <button
            onClick={() => dismissWorkflow(workflow.carId)}
            className="p-1 rounded-md hover:bg-muted transition-colors cursor-pointer"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        )}
      </div>

      {/* Logs */}
      <div className="max-h-[240px] overflow-y-auto">
        <div className="p-2 space-y-1">
          {workflow.logs.length === 0 && workflow.status === "running" && (
            <div className="flex items-center justify-center gap-2 py-6 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Waiting for agent response...
            </div>
          )}
          {workflow.logs.map((log, i) => (
            <LogEntry key={`${log.seq}-${i}`} log={log} />
          ))}
          <div ref={logsEndRef} />
        </div>
      </div>

      {/* Result summary */}
      {workflow.resultSummary && isTerminal && (
        <div className="px-4 py-2.5 border-t border-border bg-background/50">
          <div className="text-[11px] text-secondary-foreground leading-relaxed prose prose-xs max-w-none break-words">
            <ReactMarkdown>{safeMd(workflow.resultSummary)}</ReactMarkdown>
          </div>
        </div>
      )}

      {/* Error */}
      {workflow.error && (
        <div className="px-4 py-2.5 border-t border-red-100 bg-red-50/50">
          <p className="text-[11px] text-red-600">{workflow.error}</p>
        </div>
      )}
    </div>
  )
}

export function WorkflowDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { workflows } = useOrchestrator()
  const all = Array.from(workflows.values()).sort((a, b) => {
    const order = { running: 0, failed: 1, completed: 2 }
    return (order[a.status] ?? 9) - (order[b.status] ?? 9)
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col gap-0 p-0">
        <DialogHeader className="px-5 py-4 border-b border-border">
          <DialogTitle className="text-base font-semibold text-foreground">
            Agent Workflows
            <span className="ml-2 text-xs font-normal text-muted-foreground">{all.length}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="p-4 space-y-3">
            {all.length === 0 ? (
              <div className="text-center py-12 text-sm text-muted-foreground">
                No active workflows
              </div>
            ) : (
              all.map((w) => <WorkflowCard key={w.carId} workflow={w} />)
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
